/**
 * QuickLogStrip — horizontal chip row showing favourite saved meals + smart recents.
 *
 * Saved-meal chips call POST /me/saved-meals/:id/log (€0, deterministic).
 * Food-recent chips call POST /me/logs/quick-add with the food's averaged macros.
 * Both update the day-totals bar via `onDayUpdate`.
 */
import type { components } from '@nutians/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../api.js';
import { RECENTS_KEY, useRecents } from '../hooks/useRecents.js';
import { SAVED_MEALS_KEY, useLogSavedMeal } from '../hooks/useSavedMeals.js';

type DaySummary = components['schemas']['DaySummary'];
type RecentItem = components['schemas']['RecentsResponse']['items'][number];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

interface QuickLogStripProps {
  onDayUpdate: (day: DaySummary) => void;
}

export function QuickLogStrip({ onDayUpdate }: QuickLogStripProps) {
  const { t } = useTranslation('log');
  const qc = useQueryClient();
  const { data: recents, isLoading } = useRecents();
  const logMeal = useLogSavedMeal();

  const quickAddMutation = useMutation({
    mutationFn: async (item: RecentItem & { type: 'food' }) => {
      const today = todayIso();
      const { data, error } = await api.POST('/me/logs/quick-add', {
        body: {
          date: today,
          label: item.name,
          kcal: item.kcal,
          proteinG: item.proteinG,
          carbsG: item.carbsG,
          fatG: item.fatG,
          fiberG: item.fiberG,
        },
      });
      if (error || !data) throw new Error('Failed to quick-add');
      return data;
    },
    onSuccess: (data) => {
      onDayUpdate(data.day);
      void qc.invalidateQueries({ queryKey: RECENTS_KEY });
    },
  });

  async function handleMealChip(id: string) {
    const today = todayIso();
    const result = await logMeal.mutateAsync({ id, date: today });
    if (result) {
      // Refresh day summary after logging saved meal.
      const { data } = await api.GET('/me/days/{date}', { params: { path: { date: today } } });
      if (data) onDayUpdate(data);
      void qc.invalidateQueries({ queryKey: SAVED_MEALS_KEY });
    }
  }

  function handleFoodChip(item: RecentItem & { type: 'food' }) {
    quickAddMutation.mutate(item);
  }

  const items = recents?.items ?? [];
  const isBusy = logMeal.isPending || quickAddMutation.isPending;

  if (isLoading || items.length === 0) {
    if (isLoading) return null;
    return <p className="text-xs text-ink-soft dark:text-ink-soft">{t('quickLog.noRecents')}</p>;
  }

  return (
    <ul className="flex gap-2 overflow-x-auto pb-1 scrollbar-none" aria-label={t('quickLog.title')}>
      {items.map((item, i) => {
        const key = item.type === 'saved_meal' ? item.id : `${item.name}-${i}`;
        const label = item.type === 'saved_meal' ? `★ ${item.name}` : item.name;
        const kcalText = `${Math.round(item.kcal)} kcal`;

        return (
          <li key={key}>
            <button
              type="button"
              disabled={isBusy}
              onClick={() => {
                if (item.type === 'saved_meal') void handleMealChip(item.id);
                else handleFoodChip(item as RecentItem & { type: 'food' });
              }}
              aria-label={`${t('quickLog.quickAdd')} — ${item.name}`}
              className="
                flex shrink-0 flex-col items-start rounded-xl border border-husk
                bg-white px-3 py-2 text-left text-xs
                hover:border-leaf hover:bg-leaf/5
                focus:outline-none focus:ring-2 focus:ring-leaf
                disabled:opacity-50 disabled:cursor-not-allowed
                dark:border-ink/20 dark:bg-shell dark:hover:border-leaf
                max-w-[120px]
              "
            >
              <span className="truncate w-full font-medium text-ink dark:text-ink">{label}</span>
              <span className="text-ink-soft dark:text-ink-soft">{kcalText}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
